<script>
import { MANAGEMENT, NORMAN } from '@shell/config/types';
import { membershipIdentity } from '@shell/utils/membership-identity';

export default {
  props: {
    value: {
      type:    String,
      default: '',
    },

    row: { type: Object, default: () => ({}) },

    useMuted: {
      type:    Boolean,
      default: true,
    },

    showLabels: {
      type:    Boolean,
      default: false,
    }
  },

  async fetch() {
    await this.loadData();
  },

  data() {
    // Load from cache immediately if possible
    return {
      principal: null, loading: false, request: 0
    };
  },

  computed: {
    visibleUsers() {
      return this.$store.getters['management/all']?.(MANAGEMENT.USER) || [];
    },
    identity() {
      const supplied = membershipIdentity({ principalId: this.value });

      return supplied.principalId ? supplied : membershipIdentity(this.row, this.visibleUsers);
    },
    principalId() {
      return this.identity.principalId;
    },
    fallbackName() {
      const matches = this.visibleUsers.filter((user) => this.principalId && user.principalIds?.includes(this.principalId));
      const bound = membershipIdentity({ userId: this.row.userId, userName: this.row.userName }, this.visibleUsers);
      const user = matches.length === 1 ? matches[0] : null;
      const boundLabel = !this.principalId || bound.principalId === this.principalId ? bound.label : '';

      return user?.displayName || user?.nameDisplay || user?.username || boundLabel || this.identity.label || this.identity.identifier || this.t('principal.identityUnavailable');
    },
    showBoth() {
      const p = this.principal;

      return p?.name && p.loginName && p.name.trim().toLowerCase() !== p.loginName.trim().toLowerCase();
    }
  },

  methods: {
    async loadData() {
      const request = ++this.request;
      const value = this.principalId;

      this.loading = true;
      this.principal = value ? this.$store.getters['rancher/byId']?.(NORMAN.PRINCIPAL, value) : null;

      if (!value) {
        this.loading = false;

        return;
      }

      if (this.principal) {
        this.loading = false;

        return;
      }

      const principalId = encodeURIComponent(value);

      try {
        const principal = await this.$store.dispatch('rancher/find', {
          type: NORMAN.PRINCIPAL,
          id:   value,
          opt:  { url: `/v3/principals/${ principalId }` }
        });

        if (request === this.request) {
          this.principal = principal;
        }
      } catch (e) {
        // Keep the binding identity visible when the provider is unavailable or access is denied.
      } finally {
        if (request === this.request) {
          this.loading = false;
        }
      }
    }
  },

  beforeUnmount() {
    this.request++;
  },

  watch: {
    principalId() {
      this.loadData();
    }
  },
};
</script>

<template>
  <div
    class="principal"
    :class="{'showLabels': showLabels}"
  >
    <template v-if="!principal && (loading || $fetchState.pending)">
      <div class="avatar">
        <div class="empty">
          <i class="icon icon-spinner icon-lg" />
        </div>
      </div>
      <div
        v-clean-html="t('principal.loading', null, true)"
        class="name"
        :class="{'text-muted': useMuted}"
      />
      <div class="description" />
    </template>

    <template v-else-if="principal">
      <div class="avatar">
        <img
          v-if="principal.profilePicture"
          :src="principal.avatarSrc"
          :class="{'round': principal.roundAvatar}"
          :alt="t('principal.alt.avatar')"
        >
        <i
          v-else
          class="icon icon-user principal-avatar-icon"
          role="img"
          :aria-label="t('principal.alt.avatar')"
        />
      </div>
      <div
        v-if="showLabels"
        class="name"
      >
        <table>
          <tbody>
            <tr><th>{{ t('principal.name') }}: </th><td>{{ principal.name || principal.loginName }}</td></tr>
            <tr><th>{{ t('principal.loginName') }}: </th><td>{{ principal.loginName }}</td></tr>
            <tr><th>{{ t('principal.type') }}: </th><td>{{ principal.displayType }}</td></tr>
          </tbody>
        </table>
      </div>
      <template v-else>
        <div class="name">
          <template v-if="showBoth">
            {{ principal.name }}
            <span
              v-if="principal.loginName"
              :class="{'text-muted': useMuted}"
            >({{ principal.loginName }})</span>
          </template>
          <template v-else-if="principal.name">
            {{ principal.name }}
          </template>
          <template v-else>
            {{ principal.loginName || principal.nameDisplay || identity.identifier }}
          </template>
        </div>
        <div
          class="description"
          :class="{'text-muted': useMuted}"
        >
          {{ principal.displayType }}
        </div>
      </template>
    </template>

    <template v-else>
      <div class="avatar">
        <div
          class="empty"
          :class="{'text-muted': useMuted}"
        >
          <i
            class="icon icon-user"
            aria-hidden="true"
          />
        </div>
      </div>
      <div
        class="name fallback-name"
      >
        {{ fallbackName }}
      </div>
      <div
        class="description"
        :class="{'text-muted': useMuted}"
      >
        <span
          v-if="identity.identifier && identity.identifier !== fallbackName"
          class="identity-id"
        >{{ identity.identifier }}</span>
        <span class="lookup-unavailable">{{ t('principal.detailsUnavailable') }}<button
          v-if="principalId"
          type="button"
          class="principal-retry"
          @click.stop="loadData"
        >{{ t('principal.retry') }}</button></span>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  $size: 36px;

  .principal {
    display: grid;
    grid-template-areas:
      "avatar name"
      "avatar description";
    grid-template-columns: $size minmax(0, 1fr);
    grid-template-rows: auto auto;
    column-gap: 10px;
    align-items: center;
    min-width: 0;

    th {
      text-align: left;
      font-weight: normal;
      padding-right: 10px;
    }

    &.showLabels {
      grid-template-areas:
        "avatar name";
      grid-template-columns: 60px auto;
      grid-template-rows: 60px;
      column-gap: 0;
      .name {
        line-height: unset;
      }

      table tr td:not(:first-of-type) {
        padding-left: 10px;
      }
    }

    .avatar {
      grid-area: avatar;
      text-align: center;

      DIV.empty {
        border: 1px solid var(--border);
        border-radius: 6px;
        width: $size;
        height: $size;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--dev-layer, var(--body-bg));
      }

      IMG {
        width: $size;
        height: $size;
      }

      .principal-avatar-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: $size;
        height: $size;
        font-size: $size;
        color: var(--body-text);
      }

      DIV.round, IMG.round {
        border-radius: 50%;
      }
    }

    .name {
      grid-area: name;
      line-height: math.div($size, 2);
      overflow-wrap: anywhere;
    }

    .description {
      grid-area: description;
      line-height: math.div($size, 2);
      overflow-wrap: anywhere;
      font-size: 12px;
    }
    .identity-id { display: block; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
    .lookup-unavailable { display: flex; flex-wrap: wrap; gap: 4px 10px; color: var(--muted); }
    .principal-retry { border: 0; padding: 0; background: transparent; color: var(--link); font: inherit; cursor: pointer; &:hover { text-decoration: underline; } &:focus-visible { outline: 2px solid var(--primary-keyboard-focus); outline-offset: 2px; } }
  }
</style>
